import { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useSearchParams, Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { useAuth, authHeaders } from '@/context/AuthContext';
import { Package, Truck, CheckCircle, Clock, Mail as MailIcon, Send, ArrowLeft, AlertCircle, SearchCheck, Paperclip, X, Image as ImageIcon, FileText } from 'lucide-react';
import { formatAuPhone } from '@/utils/phone';
import AccountSidebar from '@/components/AccountSidebar';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_STEPS = [
  { key: 'order_received', label: 'Order Received', icon: Package },
  { key: 'under_review', label: 'Under Review', icon: SearchCheck },
  { key: 'in_production', label: 'In Production', icon: Clock },
  { key: 'shipped', label: 'Shipped', icon: Truck },
];

const CUSHION_THUMBNAILS = {
  'bay-window-bench-seat-cushion': '/assets/thumbnails/bay-window-bench-seat-cushion.png',
  'rectangle-bench-seat-cushion': '/assets/thumbnails/rectangle-bench-seat-cushion.png',
  'bench-seat': '/assets/thumbnails/rectangle-bench-seat-cushion.png',
  'round-bench-seat-cushion': '/assets/thumbnails/round-bench-seat-cushion.png',
  'wedge-back-cushion': '/assets/thumbnails/wedge-back-cushion.png',
  'rounded-corners-bench-seat-cushion': '/assets/thumbnails/rounded-corners-bench-seat-cushion.png',
  'chair-bench-seat-cushion': '/assets/thumbnails/chair-bench-seat-cushion.png',
  'round-back-chair-seat-cushion': '/assets/thumbnails/round-back-chair-seat-cushion.png',
  'round-back-seat': '/assets/thumbnails/round-back-chair-seat-cushion.png',
  'round-back-bench': '/assets/thumbnails/round-back-chair-seat-cushion.png',
  't-shaped-bench-seat-cushion': '/assets/thumbnails/t-shaped-bench-seat-cushion.png',
  'l-shaped-bench-seat-cushion': '/assets/thumbnails/l-shaped-bench-seat-cushion.png',
  'waterfall-edge-bench-seat-cushion': '/assets/thumbnails/waterfall-edge-bench-seat-cushion.png',
  'waterfall-edge-cushion': '/assets/thumbnails/waterfall-edge-bench-seat-cushion.png',
  'waterfall-edge-back-cushion': '/assets/thumbnails/waterfall-edge-back-cushion.png',
  'scatter-cushion': '/assets/thumbnails/scatter-cushion.png',
  'bolster-cushion': '/assets/thumbnails/bolster-cushion.png',
  'bench-back-cushion': '/assets/thumbnails/waterfall-edge-back-cushion.png',
  'premium-cushion': '/assets/thumbnails/rectangle-bench-seat-cushion.png',
};

const ZIP_LABELS = { back: 'Back Edge', front: 'Front Edge', left: 'Left Edge', right: 'Right Edge', length: 'Along Length', width: 'Along Width' };
const PIPING_LABELS = { same: 'Same Material', different: 'Different Material' };
const PTYPE_LABELS = { cover_and_foam: 'Cover & Foam Insert', cover_only: 'Cover Only', foam_only: 'Foam Insert Only' };

const statusLabel = (s) => s?.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()) || '';

export default function OrderDetailPage() {
  const { orderId } = useParams();
  const [searchParams] = useSearchParams();
  const guestEmail = searchParams.get('email');
  const { user, loading: authLoading } = useAuth();
  const [order, setOrder] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMsg, setNewMsg] = useState('');
  const [loading, setLoading] = useState(true);
  const [sendingMsg, setSendingMsg] = useState(false);
  const [attachment, setAttachment] = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);
  const msgRef = useRef(null);
  const msgEndRef = useRef(null);
  const markedReadRef = useRef(false);

  useEffect(() => { if (!authLoading) { fetchOrder(); fetchMessages(); } }, [orderId, authLoading]);
  const justSentRef = useRef(false);
  useEffect(() => {
    if (justSentRef.current) { msgEndRef.current?.scrollIntoView({ behavior: 'smooth' }); justSentRef.current = false; }
  }, [messages]);

  // Mark messages as read when message area is visible
  useEffect(() => {
    if (!msgRef.current || markedReadRef.current) return;
    const unread = messages.filter(m => m.sender === 'admin' && !m.read);
    if (unread.length === 0) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !markedReadRef.current) {
          markedReadRef.current = true;
          fetch(`${API}/orders/${orderId}/messages/read`, { method: 'PUT', headers: authHeaders() })
            .then(() => {
              setMessages(prev => prev.map(m => m.sender === 'admin' ? { ...m, read: true } : m));
            })
            .catch(() => {});
          observer.disconnect();
        }
      },
      { threshold: 0.3 }
    );
    observer.observe(msgRef.current);
    return () => observer.disconnect();
  }, [messages, orderId]);

  const fetchOrder = async () => {
    setLoading(true);
    try {
      let res;
      if (user) {
        res = await fetch(`${API}/orders/${orderId}`, { headers: authHeaders() });
      } else if (guestEmail) {
        res = await fetch(`${API}/orders/track`, {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ order_id: orderId, email: guestEmail }),
        });
      } else {
        setLoading(false); return;
      }
      if (res.ok) setOrder(await res.json());
    } catch { /* ignore */ }
    setLoading(false);
  };

  const fetchMessages = async () => {
    try {
      const res = await fetch(`${API}/orders/${orderId}/messages`, { headers: authHeaders() });
      if (res.ok) setMessages(await res.json());
    } catch { /* ignore */ }
  };

  const sendMessage = async () => {
    if (!newMsg.trim() && !attachment) return;
    setSendingMsg(true);
    let attachmentData = null;

    if (attachment) {
      setUploading(true);
      const formData = new FormData();
      formData.append('file', attachment);
      try {
        const upRes = await fetch(`${API}/orders/${orderId}/messages/upload`, { method: 'POST', headers: authHeaders(), body: formData });
        if (upRes.ok) {
          attachmentData = await upRes.json();
        } else {
          toast.error('Failed to upload attachment');
          setUploading(false); setSendingMsg(false); return;
        }
      } catch { toast.error('Upload failed'); setUploading(false); setSendingMsg(false); return; }
      setUploading(false);
    }

    try {
      const body = { content: newMsg.trim() || (attachmentData ? `Attachment: ${attachmentData.file_name}` : '') };
      if (attachmentData) body.attachment = attachmentData;
      const res = await fetch(`${API}/orders/${orderId}/messages`, {
        method: 'POST', headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify(body),
      });
      if (res.ok) { setNewMsg(''); setAttachment(null); justSentRef.current = true; fetchMessages(); toast.success('Message sent'); }
    } catch { toast.error('Failed to send'); }
    setSendingMsg(false);
  };

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) { toast.error('File too large (max 10MB)'); return; }
    setAttachment(file);
  };

  const isImageUrl = (url) => /\.(jpg|jpeg|png|gif|webp)$/i.test(url);

  const getStepIndex = () => {
    const idx = STATUS_STEPS.findIndex(s => s.key === order?.status);
    return idx >= 0 ? idx : 0;
  };

  if (loading) return <div className="pt-20 sm:pt-[122px] min-h-[60vh] flex items-center justify-center"><div className="animate-spin rounded-full h-8 w-8 border-t-2 border-bush" /></div>;
  if (!order) return (
    <div className="pt-20 sm:pt-[122px] min-h-[60vh] flex items-center justify-center px-4">
      <div className="text-center">
        <AlertCircle className="w-10 h-10 text-gray-300 mx-auto mb-3" />
        <p className="text-gray-500">Order not found</p>
        <Link to="/track-order" className="text-bush text-sm hover:underline mt-2 inline-block">Try tracking again</Link>
      </div>
    </div>
  );

  const currentStep = getStepIndex();
  const unreadCount = messages.filter(m => m.sender === 'admin' && !m.read).length;

  const scrollToMessages = () => {
    msgRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <div className="pt-20 sm:pt-[122px] min-h-screen bg-cream" data-testid="order-detail-page">
      <section className="bg-white py-10 sm:py-16 border-b border-cream-dark">
        <div className="max-w-[1632px] mx-auto px-5 sm:px-8 lg:px-12">
          <h1 className="text-4xl sm:text-5xl font-display font-semibold text-[#1A1A1A] mb-4 tracking-tight">Order Details</h1>
          <p className="text-base sm:text-lg text-gray-500 max-w-2xl font-body">View your order details, status updates, and messages.</p>
        </div>
      </section>

      <div className="max-w-5xl mx-auto px-4 sm:px-8 py-6 sm:py-12">
        <div className="flex flex-col sm:flex-row gap-6">
          {user && <AccountSidebar />}

          <div className="flex-1 min-w-0">
      <Link to={user ? '/account' : '/track-order'} className="inline-flex items-center text-sm text-gray-500 hover:text-bush mb-4">
        <ArrowLeft className="w-4 h-4 mr-1" /> {user ? 'Back to My Orders' : 'Track Another Order'}
      </Link>

      {/* Unread message notification */}
      {unreadCount > 0 && (
        <button
          onClick={scrollToMessages}
          className="w-full mb-4 flex items-center gap-3 p-3 bg-bush/10 border border-bush/20 rounded-xl text-sm text-bush hover:bg-bush/15 transition-colors"
          data-testid="unread-notification"
        >
          <MailIcon className="w-5 h-5 flex-shrink-0" />
          <span className="font-medium">You have {unreadCount} unread message{unreadCount > 1 ? 's' : ''}</span>
          <span className="ml-auto text-xs opacity-70">View</span>
        </button>
      )}

      {/* Header */}
      <div className="bg-white rounded-xl shadow-card p-5 sm:p-6 mb-4">
        <span className={`inline-block text-xs px-3 py-1.5 rounded-full font-semibold mb-3 ${order.status === 'delivered' ? 'bg-green-100 text-green-700' : order.status === 'cancelled' ? 'bg-red-100 text-red-700' : 'bg-bush/10 text-bush'}`} data-testid="order-status-badge">
          {statusLabel(order.status)}
        </span>
        <div>
          <h1 className="text-xl sm:text-2xl font-serif font-bold" data-testid="order-id-heading">Order ID: {order.order_id}</h1>
          <p className="text-sm text-gray-400 mt-0.5">Placed {new Date(order.created_at).toLocaleDateString('en-AU', { day: 'numeric', month: 'long', year: 'numeric' })}</p>
        </div>

        {/* Progress bar */}
        <div className="mt-6 mb-2">
          <div className="flex items-center justify-between">
            {STATUS_STEPS.map((step, i) => (
              <div key={step.key} className="flex flex-col items-center flex-1">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center ${i <= currentStep ? 'bg-bush text-white' : 'bg-gray-100 text-gray-400'}`}>
                  <step.icon className="w-4 h-4" />
                </div>
                <p className={`text-[10px] sm:text-xs mt-1 text-center ${i <= currentStep ? 'text-bush font-medium' : 'text-gray-400'}`}>{step.label}</p>
              </div>
            ))}
          </div>
          <div className="flex mt-2 mx-4">
            {[0, 1, 2].map(i => (
              <div key={i} className={`flex-1 h-1 rounded ${i < currentStep ? 'bg-bush' : 'bg-gray-100'} ${i > 0 ? 'ml-1' : ''}`} />
            ))}
          </div>
        </div>

        {/* Status history */}
        {order.status_history?.length > 0 && (
          <div className="mt-4 border-t border-gray-100 pt-4">
            <p className="text-sm font-semibold text-gray-700 mb-3">Status History</p>
            <div className="relative pl-5">
              <div className="absolute left-[7px] top-1 bottom-1 w-px bg-gray-200" />
              {order.status_history.map((h, i) => (
                <div key={i} className="relative flex items-start gap-3 pb-4 last:pb-0">
                  <div className={`absolute left-[-13px] top-1 w-2.5 h-2.5 rounded-full border-2 ${i === order.status_history.length - 1 ? 'bg-bush border-bush' : 'bg-white border-gray-300'}`} />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-800">{statusLabel(h.status)}</p>
                    {h.note && <p className="text-xs text-gray-500 mt-0.5">{h.note}</p>}
                    <p className="text-xs text-gray-400 mt-0.5">{new Date(h.timestamp).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Messages — moved above the Items list */}
      <div ref={msgRef} className="bg-white rounded-xl shadow-card p-5 sm:p-6 mb-4">
        <h2 className="text-base font-serif font-bold flex items-center gap-2 mb-4">
          <MailIcon className="w-4 h-4" /> Messages
          {messages.length > 0 && <span className="text-xs bg-bush/10 text-bush px-2 py-0.5 rounded-full">{messages.length}</span>}
        </h2>

        <div className={`${messages.length > 4 ? 'max-h-[32rem]' : 'max-h-80'} overflow-y-auto space-y-4 mb-4`}>
          {messages.length === 0 && <p className="text-sm text-gray-400 text-center py-4">No messages yet. Start a conversation about your order.</p>}
          {messages.map(m => (
            <div key={m.message_id} className={`flex pb-3 ${m.sender === 'customer' ? 'justify-end' : 'justify-start'}`}>
              <div className={`max-w-[80%] rounded-xl px-3.5 py-2.5 ${m.sender === 'customer' ? 'bg-bush text-white' : 'bg-gray-100 text-gray-800'}`}>
                <p className={`text-[10px] font-medium mb-1 ${m.sender === 'customer' ? 'opacity-70' : 'text-gray-400'}`}>
                  {m.sender_name} &middot; {new Date(m.created_at).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
                </p>
                {m.attachment && m.attachment.file_url && (
                  <div className="mb-1.5">
                    {isImageUrl(m.attachment.file_url) ? (
                      <a href={m.attachment.file_url} target="_blank" rel="noopener noreferrer">
                        <img src={m.attachment.file_url} alt={m.attachment.file_name} className="max-w-full max-h-48 rounded-lg border border-white/20" />
                      </a>
                    ) : (
                      <a href={m.attachment.file_url} target="_blank" rel="noopener noreferrer"
                        className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-medium ${m.sender === 'customer' ? 'bg-white/20 text-white hover:bg-white/30' : 'bg-white text-gray-700 hover:bg-gray-50 border border-gray-200'}`}>
                        <FileText className="w-3.5 h-3.5" />{m.attachment.file_name}
                      </a>
                    )}
                  </div>
                )}
                {m.content && <p className="text-sm">{m.content}</p>}
              </div>
            </div>
          ))}
          <div ref={msgEndRef} />
        </div>

        {/* Attachment preview */}
        {attachment && (
          <div className="mb-3 flex items-center gap-2 p-2 bg-gray-50 rounded-lg border border-gray-200">
            {attachment.type.startsWith('image/') ? <ImageIcon className="w-4 h-4 text-gray-500" /> : <FileText className="w-4 h-4 text-gray-500" />}
            <span className="text-xs text-gray-600 truncate flex-1">{attachment.name}</span>
            <button onClick={() => setAttachment(null)} className="text-gray-400 hover:text-red-500"><X className="w-3.5 h-3.5" /></button>
          </div>
        )}

        <div className="flex gap-2">
          <input type="file" ref={fileInputRef} className="hidden" accept="image/*,.pdf,.doc,.docx,.txt" onChange={handleFileSelect} />
          <Button variant="ghost" size="icon" onClick={() => fileInputRef.current?.click()} className="h-10 w-10 flex-shrink-0 text-gray-400 hover:text-bush" data-testid="attach-file-btn">
            <Paperclip className="w-4 h-4" />
          </Button>
          <Input
            placeholder="Type a message..."
            value={newMsg} onChange={e => setNewMsg(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && sendMessage()}
            className="flex-1 h-10" data-testid="message-input"
          />
          <Button onClick={sendMessage} disabled={sendingMsg || uploading || (!newMsg.trim() && !attachment)} size="icon" className="h-10 w-10 bg-bush hover:bg-bush/90 flex-shrink-0" data-testid="send-message-btn">
            <Send className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* Order Details — labeled table of customer & shipping info */}
      <div className="bg-white rounded-xl shadow-card p-5 sm:p-6 mb-4" data-testid="order-details-block">
        <h2 className="text-base font-serif font-bold mb-4">Order Details</h2>
        <table className="w-full text-sm" data-testid="order-details-table">
          <tbody className="divide-y divide-gray-100">
            <DetailRow label="Name" value={order.customer?.name || '—'} />
            <DetailRow label="Email" value={order.customer?.email || '—'} />
            <DetailRow label="Phone" value={order.customer?.phone ? formatAuPhone(order.customer.phone) : '—'} />
            {order.customer?.address?.line1 && (
              <DetailRow
                label="Shipping address"
                value={
                  <span className="block leading-relaxed">
                    {order.customer.address.line1}
                    {order.customer.address.line2 && (<><br />{order.customer.address.line2}</>)}
                    <br />
                    {[order.customer.address.city, order.customer.address.state, order.customer.address.postcode].filter(Boolean).join(', ')}
                    {order.customer.address.country && (<><br />{order.customer.address.country}</>)}
                  </span>
                }
              />
            )}
            <DetailRow label="Order placed" value={new Date(order.created_at).toLocaleDateString('en-AU', { day: 'numeric', month: 'long', year: 'numeric' })} />
          </tbody>
        </table>
      </div>

      {/* Items */}
      <div className="bg-white rounded-xl shadow-card p-5 sm:p-6 mb-4">
        <h2 className="text-base font-serif font-bold mb-3">Items</h2>
        <div className="space-y-4">
          {order.items?.map((item, i) => {
            const dims = item.dimensions || {};
            const dimParts = [];
            if (dims.length) dimParts.push(`${dims.length} cm L`);
            if (dims.width) dimParts.push(`${dims.width} cm W`);
            if (dims.thickness) dimParts.push(`${dims.thickness} cm T`);
            if (dims.diameter) dimParts.push(`${dims.diameter} cm dia`);
            if (dims.straight_depth) dimParts.push(`${dims.straight_depth} cm SD`);
            if (dims.back_width) dimParts.push(`${dims.back_width} cm BW`);
            if (dims.back_height) dimParts.push(`${dims.back_height} cm BH`);
            if (dims.base_depth) dimParts.push(`${dims.base_depth} cm BD`);
            if (dims.top_depth) dimParts.push(`${dims.top_depth} cm TD`);
            if (dims.front_width) dimParts.push(`${dims.front_width} cm FW`);
            const dimStr = dimParts.join('  x  ');
            return (
              <div key={i} className="py-3 border-b border-gray-50 last:border-0" data-testid={`order-item-${i}`}>
                {item.preview_image && (
                  <div className="mb-3 rounded-lg overflow-hidden w-full sm:max-w-[300px]">
                    <img src={`${process.env.REACT_APP_BACKEND_URL}${item.preview_image}`} alt={item.cushion_name} className="w-full h-auto rounded-lg" />
                  </div>
                )}
                <div className="flex justify-between items-start mb-1.5">
                  <span className="font-semibold text-sm text-gray-900">{item.cushion_name || 'Item'}</span>
                  <span className="font-semibold text-sm">${item.total_price?.toFixed(2)}</span>
                </div>
                <div className="text-xs text-gray-500 mt-1.5 flex flex-col gap-1">
                  {dimStr && <span className="px-1.5 py-0.5 bg-gray-100 rounded text-[10px] self-start">{dimStr}</span>}
                  {item.product_type && <span className="px-1.5 py-0.5 bg-gray-100 rounded text-[10px] self-start">{PTYPE_LABELS[item.product_type] || item.product_type}</span>}
                  {item.fabric_name && <span className="px-1.5 py-0.5 bg-gray-100 rounded text-[10px] self-start">Fabric: {item.fabric_name}</span>}
                  {item.foam_name && <span className="px-1.5 py-0.5 bg-gray-100 rounded text-[10px] self-start">Foam: {item.foam_name}</span>}
                  {item.zip_placement && <span className="px-1.5 py-0.5 bg-gray-100 rounded text-[10px] self-start">Zip: {ZIP_LABELS[item.zip_placement] || item.zip_placement}</span>}
                  {item.piping_option && (
                    <span className="px-1.5 py-0.5 bg-gray-100 rounded text-[10px] self-start">
                      Piping: {PIPING_LABELS[item.piping_option] || item.piping_option}
                      {item.piping_fabric_name && item.piping_option === 'different' ? ` (${item.piping_fabric_name})` : ''}
                    </span>
                  )}
                  {item.configuration?.facing && <span className="px-1.5 py-0.5 bg-gray-100 rounded text-[10px] self-start">Facing: {item.configuration.facing}</span>}
                  {item.configuration?.addMiddle && <span className="px-1.5 py-0.5 bg-gray-100 rounded text-[10px] self-start">Middle Piece: {item.configuration.middleWidth} cm</span>}
                  <span className="px-1.5 py-0.5 bg-gray-100 rounded text-[10px] self-start">Qty: {item.quantity}</span>
                </div>
              </div>
            );
          })}
        </div>
        <div className="flex justify-between mt-4 pt-3 border-t border-gray-100">
          <span className="font-semibold">Total</span>
          <span className="font-bold text-lg">${order.total?.toFixed(2)}</span>
        </div>
      </div>
          </div>
        </div>
      </div>
    </div>
  );
}

const DetailRow = ({ label, value }) => (
  <tr>
    <th className="text-left align-top py-2.5 pr-4 font-medium text-gray-500 w-[140px] sm:w-[180px]">{label}</th>
    <td className="text-left align-top py-2.5 text-gray-900 font-body">{value}</td>
  </tr>
);
